/**
 * Interleaves two arrays, alternating elements from each array.
 * Starts with the first array, then the second, and so on.
 *
 * @param firstArray - The first array to interleave
 * @param secondArray - The second array to interleave
 * @returns A new array with interleaved elements
 *
 * @example
 * interleaveArrays([1, 3, 5], [2, 4, 6]) // [1, 2, 3, 4, 5, 6]
 * interleaveArrays([1, 3], [2, 4, 6, 8]) // [1, 2, 3, 4, 6, 8]
 */
export function interleaveArrays<T>(firstArray: T[], secondArray: T[]): T[] {
  const result: T[] = [];
  const maxLength = Math.max(firstArray.length, secondArray.length);

  for (let i = 0; i < maxLength; i++) {
    if (i < firstArray.length) {
      result.push(firstArray[i]);
    }
    if (i < secondArray.length) {
      result.push(secondArray[i]);
    }
  }

  return result;
}

/**
 * Inserts each element of `insertions` into `base` at the given 1-based
 * positions. If a position is missing or past the current end, the element is
 * appended. Ported to match eb-ui's `interleaveTwoArrays` so both frontends
 * place ads identically when the denser layout ships.
 *
 * @example
 * // default related-events layout (ads at positions 1, 3)
 * interleaveAtPositions([o1, o2, o3, o4], [p1, p2], [1, 3])
 * // [p1, o1, p2, o2, o3, o4]
 *
 * @example
 * // dense layout (ads at positions 1, 2, 4)
 * interleaveAtPositions([o1, o2, o3, o4], [p1, p2, p3], [1, 2, 4])
 * // [p1, p2, o1, p3, o2, o3, o4]
 */
export function interleaveAtPositions<T>(base: T[], insertions: T[], positions: number[]): T[] {
  const result = [...base];

  insertions.forEach((item, index) => {
    const targetPosition = positions[index] ?? result.length + 1;
    const insertIndex = Math.min(targetPosition - 1, result.length);
    result.splice(insertIndex, 0, item);
  });

  return result;
}
